// Lista clienti/aziende con import Excel e creazione nuova azienda
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Building2, Search, Plus, Upload, Download, Trash2 } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { EmptyState } from '../../components/ui/EmptyState'
import { CardSkeleton } from '../../components/ui/Skeleton'
import api from '../../lib/api'

interface Company {
  id: string
  name: string
  vatNumber?: string
  email?: string
  phone?: string
  openTickets?: number
  totalSites?: number
}

export function CompaniesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')

  const { data: allCompanies = [], isLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      try {
        const res = await api.get<Company[]>('/companies')
        return res.data ?? []
      } catch {
        return [] as Company[]
      }
    },
  })

  // Filtra lato client
  const companies = search
    ? allCompanies.filter(
        (c) =>
          c.name?.toLowerCase().includes(search.toLowerCase()) ||
          c.vatNumber?.includes(search)
      )
    : allCompanies

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/companies/${id}`)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies'] })
    },
  })

  const handleDelete = (e: React.MouseEvent, company: Company) => {
    e.stopPropagation()
    if (window.confirm(`Eliminare il cliente "${company.name}" e tutti i suoi subappalti?`)) {
      deleteMutation.mutate(company.id)
    }
  }

  // Download template Excel tramite link
  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/companies/import/template', { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data as BlobPart]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'kleisnote-import-aziende.xlsx')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      // Silenzioso
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Clienti"
        subtitle={`${companies.length} clienti`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={() => void handleDownloadTemplate()}
            >
              Scarica template
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Upload className="h-4 w-4" />}
              onClick={() => navigate('/companies/import')}
            >
              Importa da Excel
            </Button>
            <Button
              size="sm"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => navigate('/companies/new')}
            >
              Nuovo cliente
            </Button>
          </div>
        }
      />

      <div className="max-w-sm">
        <Input
          placeholder="Cerca per nome o P.IVA..."
          leftIcon={<Search className="h-4 w-4" />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
      ) : companies.length === 0 ? (
        <EmptyState
          icon={<Building2 className="h-12 w-12" />}
          title="Nessun cliente trovato"
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {companies.map((company) => (
            <div
              key={company.id}
              className="relative rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] p-4 hover:border-[var(--fc-primary)] hover:shadow-[var(--fc-shadow)] transition-all"
            >
              <button
                className="text-left w-full"
                onClick={() => navigate(`/companies/${company.id}`)}
                aria-label={`Vedi dettaglio ${company.name}`}
              >
                <div className="flex items-center gap-3 mb-3 pr-8">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[color-mix(in_srgb,var(--fc-primary)_10%,transparent)]">
                    <Building2 className="h-5 w-5 text-[var(--fc-primary)]" aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-[var(--fc-foreground)] truncate">{company.name}</p>
                    {company.vatNumber && (
                      <p className="text-xs text-[var(--fc-foreground-muted)]">P.IVA {company.vatNumber}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 text-xs text-[var(--fc-foreground-muted)]">
                  <Badge variant="default">{company.openTickets ?? 0} ticket aperti</Badge>
                  <span>{company.totalSites ?? 0} siti</span>
                </div>
              </button>
              <button
                onClick={(e) => handleDelete(e, company)}
                className="absolute top-3 right-3 flex h-7 w-7 items-center justify-center rounded text-[var(--fc-foreground-muted)] hover:bg-red-50 hover:text-[var(--fc-danger)] transition-colors"
                aria-label={`Elimina ${company.name}`}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
